import React, { useEffect, useState } from 'react';
import { CircleNotch, FilePdf, Eye, Plus, Trash, X, FloppyDisk, EyeSlash, Files, Folder, DownloadSimple } from '@phosphor-icons/react';
import { downloadDocument } from '../../utils/downloadDocument';
import axios from '../../utils/axiosInstance';
import VaultTab from '../shared/VaultTab';

const API = `${window.location.origin}/api`;

const MONTHS = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

function SpecyfikacjeView({ documents, onViewDocument }) {
    if (documents.length === 0) {
        return <div className="text-center py-20 text-muted-foreground">Brak dokumentów.</div>;
    }
    const grouped = documents.reduce((acc, doc) => {
        const periodMatch = doc.period?.match(/^(\d{2})-(\d{2})\.(\d{2})\.(\d{4})$/);
        if (!periodMatch) return acc;
        const [, startDay, , month, year] = periodMatch;
        const periodLabel = startDay === '01' ? '01-15' : '16-30';
        if (!acc[year]) acc[year] = {};
        if (!acc[year][month]) acc[year][month] = {};
        if (!acc[year][month][periodLabel]) acc[year][month][periodLabel] = [];
        acc[year][month][periodLabel].push(doc);
        return acc;
    }, {});
    const years = Object.keys(grouped).sort((a, b) => b - a);

    return (
        <div className="space-y-8">
            {years.map(year => (
                <div key={year} className="bg-white border border-border rounded-sm p-6">
                    <h3 className="font-heading text-lg font-bold mb-4">Rok {year}</h3>
                    <div className="space-y-6">
                        {Object.keys(grouped[year]).sort((a, b) => parseInt(b) - parseInt(a)).map(month => (
                            <div key={month}>
                                <h4 className="font-semibold text-base mb-3">{MONTHS[parseInt(month) - 1]}</h4>
                                <div className="space-y-4 pl-4">
                                    {Object.keys(grouped[year][month]).sort().map(period => (
                                        <div key={period}>
                                            <h5 className="text-sm font-medium text-muted-foreground mb-2">Okres: {period}</h5>
                                            <div className="space-y-2 pl-4">
                                                {grouped[year][month][period].map(doc => (
                                                    <div key={doc.id} className="flex items-center justify-between p-3 bg-muted rounded-sm hover:bg-muted/80 transition-colors">
                                                        <div className="flex items-center gap-3">
                                                            <FilePdf className="w-5 h-5 text-red-600" />
                                                            <div>
                                                                <div className="font-medium text-sm">{doc.employee_name}</div>
                                                                <div className="text-xs text-muted-foreground">{doc.filename}</div>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => onViewDocument(doc.id)}
                                                            className="btn-secondary flex items-center gap-2 text-sm"
                                                        >
                                                            <Eye className="w-4 h-4" /> Pokaż
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function InneView({ onViewDocument }) {
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ employee_id: '', name: '', file: null, visible_to_employee: false });

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [docs, emps] = await Promise.all([
                axios.get(`${API}/documents?category=inne`),
                axios.get(`${API}/employees`),
            ]);
            setItems(docs.data || []);
            setEmployees(emps.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const handleSave = async () => {
        if (!form.employee_id) return alert('Wybierz pracownika');
        if (!form.name) return alert('Wpisz nazwę dokumentu');
        if (!form.file) return alert('Wybierz plik');
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('employee_id', form.employee_id);
            fd.append('name', form.name);
            fd.append('visible_to_employee', form.visible_to_employee ? 'true' : 'false');
            fd.append('file', form.file);
            await axios.post(`${API}/documents/upload-other`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setShowModal(false);
            setForm({ employee_id: '', name: '', file: null, visible_to_employee: false });
            fetchAll();
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.detail || 'Błąd zapisu');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Usunąć ten dokument?')) return;
        try {
            await axios.delete(`${API}/documents/${id}`);
            fetchAll();
        } catch (e) {
            alert(e.response?.data?.detail || 'Błąd usuwania');
        }
    };

    const toggleVisibility = async (doc) => {
        try {
            await axios.patch(`${API}/documents/${doc.id}`, {
                visible_to_employee: !doc.visible_to_employee,
            });
            fetchAll();
        } catch (e) {
            alert(e.response?.data?.detail || 'Błąd zapisu');
        }
    };

    const empName = (id) => {
        const e = employees.find((x) => x._id === id);
        if (!e) return '—';
        return e.imie_nazwisko ? `${e.imie_nazwisko} (${e.name})` : e.name;
    };

    if (loading) return <div className="text-center py-12"><CircleNotch className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>;

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-slate-600">{items.length} dokument(ów)</div>
                <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2" data-testid="inne-add-btn">
                    <Plus className="w-4 h-4" /> Dodaj dokument
                </button>
            </div>

            {items.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground bg-white border border-border rounded-sm">
                    Brak dokumentów w tej kategorii.
                </div>
            ) : (
                <div className="bg-white border border-border rounded-sm overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left text-xs font-semibold text-slate-600 uppercase">
                                <th className="px-4 py-3">Nazwa</th>
                                <th className="px-4 py-3">Pracownik</th>
                                <th className="px-4 py-3">Plik</th>
                                <th className="px-4 py-3">Widoczne dla pracownika</th>
                                <th className="px-4 py-3 text-right">Akcje</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {items.map((d) => (
                                <tr key={d.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 font-medium text-slate-800">{d.name || d.filename}</td>
                                    <td className="px-4 py-3 text-slate-600 text-xs">{empName(d.employee_id)}</td>
                                    <td className="px-4 py-3 text-slate-600 text-xs">{d.filename}</td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => toggleVisibility(d)}
                                            className={`px-2 py-1 rounded-md text-xs font-semibold inline-flex items-center gap-1 ${d.visible_to_employee ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                                            data-testid={`inne-vis-toggle-${d.id}`}
                                        >
                                            {d.visible_to_employee ? <><Eye className="w-3 h-3" /> Tak</> : <><EyeSlash className="w-3 h-3" /> Nie</>}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end gap-1">
                                            <button
                                                onClick={() => onViewDocument(d.id)}
                                                title="Pokaż"
                                                className="p-1.5 hover:bg-muted text-foreground rounded-sm"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => downloadDocument(d.id)}
                                                title="Pobierz"
                                                className="p-1.5 hover:bg-muted text-foreground rounded-sm"
                                                data-testid={`inne-download-${d.id}`}
                                            >
                                                <DownloadSimple className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(d.id)}
                                                title="Usuń"
                                                className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm"
                                                data-testid={`inne-delete-${d.id}`}
                                            >
                                                <Trash className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-sm w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" data-testid="inne-modal">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-heading text-xl font-bold">Nowy dokument</h3>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-muted rounded-sm">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="field-label block mb-2">Pracownik *</label>
                                <select
                                    value={form.employee_id}
                                    onChange={(e) => setForm((p) => ({ ...p, employee_id: e.target.value }))}
                                    className="input-field"
                                    data-testid="inne-employee-select"
                                >
                                    <option value="">— Wybierz —</option>
                                    {employees.map((emp) => (
                                        <option key={emp._id} value={emp._id}>
                                            {emp.imie_nazwisko ? `${emp.imie_nazwisko} (${emp.name})` : emp.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="field-label block mb-2">Nazwa dokumentu *</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                                    placeholder="np. Umowa o pracę, Zaświadczenie..."
                                    className="input-field"
                                    data-testid="inne-name-input"
                                />
                            </div>
                            <div>
                                <label className="field-label block mb-2">Plik *</label>
                                <input
                                    type="file"
                                    onChange={(e) => setForm((p) => ({ ...p, file: e.target.files?.[0] || null }))}
                                    className="input-field"
                                    data-testid="inne-file-input"
                                />
                            </div>
                            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-md bg-slate-50 border border-slate-200 hover:bg-slate-100">
                                <input
                                    type="checkbox"
                                    checked={form.visible_to_employee}
                                    onChange={(e) => setForm((p) => ({ ...p, visible_to_employee: e.target.checked }))}
                                    className="mt-1"
                                    data-testid="inne-visible-checkbox"
                                />
                                <div>
                                    <div className="text-sm font-semibold text-slate-800">Widoczne dla pracownika</div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        Jeśli zaznaczone, pracownik zobaczy ten dokument u siebie w zakładce Dokumenty.
                                    </div>
                                </div>
                            </label>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button onClick={() => setShowModal(false)} className="btn-secondary flex-1">Anuluj</button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="btn-primary flex-1 flex items-center justify-center gap-2"
                                data-testid="inne-save-btn"
                            >
                                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <FloppyDisk className="w-4 h-4" />}
                                Zapisz
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function DocumentsTab({ documents, loading, onViewDocument }) {
    const [subTab, setSubTab] = useState('specyfikacje');
    const [backfilling, setBackfilling] = React.useState(false);

    // documents from parent contains ALL documents (legacy + inne). Filter here.
    const specyfikacje = (documents || []).filter((d) => !d.category || d.category === 'specyfikacje');

    const handleBackfill = async () => {
        if (!window.confirm(
            'Przenieść wszystkie stare dokumenty bez folderu do Foldery → Pracownicy → Specyfikacje DPD/{rok}/{miesiąc}?\n\n' +
            'Dokumenty bez wykrywalnej daty trafią do "Specyfikacje DPD/Nieokreślone".\n\n' +
            'Operacja jest bezpieczna — nie usuwa plików, tylko dopisuje scope + folder.'
        )) return;
        setBackfilling(true);
        try {
            const { data } = await axios.post(`${API}/documents/backfill-vault`);
            alert(`Zrobione!\nPrzeniesiono: ${data.moved}\nPominięto: ${data.skipped}\nUtworzono folderów: ${data.folders_created}\nZnormalizowano: ${data.normalized ?? 0}`);
            // Reload after backfill — simplest refresh
            window.location.reload();
        } catch (err) {
            console.error(err);
            alert(`Błąd: ${err.response?.data?.detail || err.message}`);
        } finally {
            setBackfilling(false);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="font-heading text-xl font-bold">Dokumenty</h2>
                <button
                    onClick={handleBackfill}
                    disabled={backfilling}
                    data-testid="documents-backfill-vault-btn"
                    className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
                    title="Przenieś legacy dokumenty do Foldery → Pracownicy"
                >
                    {backfilling ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Folder className="w-4 h-4" weight="fill" />}
                    {backfilling ? 'Sortowanie...' : 'Posortuj do folderów'}
                </button>
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-1 border-b border-slate-200 mb-6">
                <button
                    onClick={() => setSubTab('specyfikacje')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${subTab === 'specyfikacje' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    data-testid="documents-subtab-specyfikacje"
                >
                    <FilePdf className="w-4 h-4 inline mr-2" /> Specyfikacje ({specyfikacje.length})
                </button>
                <button
                    onClick={() => setSubTab('inne')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${subTab === 'inne' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    data-testid="documents-subtab-inne"
                >
                    <Files className="w-4 h-4 inline mr-2" /> Inne
                </button>
                <button
                    onClick={() => setSubTab('foldery')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${subTab === 'foldery' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    data-testid="documents-subtab-foldery"
                >
                    <Folder className="w-4 h-4 inline mr-2" weight="fill" /> Foldery
                </button>
            </div>

            {subTab === 'specyfikacje' && (
                loading ? (
                    <div className="flex items-center justify-center py-20">
                        <CircleNotch className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <SpecyfikacjeView documents={specyfikacje} onViewDocument={onViewDocument} />
                )
            )}

            {subTab === 'inne' && <InneView onViewDocument={onViewDocument} />}
            {subTab === 'foldery' && <VaultTab />}
        </div>
    );
}
